<template>
  <!-- Организации используются для распределения целевых мест по конкурсам. -->
  <v-card class="target-organizations" elevation="0">
    <v-card-title class="target-organizations__header">
      <div>
        <h2 class="target-organizations__title">Целевой набор</h2>
        <p class="target-organizations__description">
          Управление организациями, направляющими абитуриентов на целевое обучение.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        :disabled="loading"
        @click="openEditor()"
      >
        Добавить организацию
      </v-btn>
    </v-card-title>

    <v-card-text class="target-organizations__content">
      <v-alert
        v-if="error"
        class="target-organizations__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-text-field
        v-if="!loading && items.length > 5"
        v-model="search"
        class="target-organizations__search"
        label="Поиск организации"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        clearable
        hide-details
      />

      <div v-if="loading">
        <v-skeleton-loader v-for="index in 4" :key="index" type="list-item" />
      </div>

      <div v-else-if="!items.length" class="target-organizations__empty">
        <v-icon icon="mdi-domain-plus" size="42" />
        <div>
          <strong>Организации пока не добавлены</strong>
          <span>Добавьте первую направляющую организацию для целевого набора.</span>
        </div>
      </div>

      <v-alert
        v-else-if="!filteredItems.length"
        type="info"
        variant="tonal"
      >
        По вашему запросу организации не найдены.
      </v-alert>

      <div v-else class="target-organizations__list">
        <article
          v-for="item in filteredItems"
          :key="item.id"
          class="target-organizations__item"
        >
          <div class="target-organizations__item-icon">
            <v-icon icon="mdi-domain" size="23" />
          </div>

          <div class="target-organizations__item-content">
            <div class="target-organizations__item-name">{{ item.name }}</div>
            <v-chip
              v-if="item.usageCount"
              class="target-organizations__usage"
              color="primary"
              variant="tonal"
              size="x-small"
            >
              Используется: {{ item.usageCount }}
            </v-chip>
          </div>

          <div class="target-organizations__item-actions">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              size="small"
              aria-label="Редактировать организацию"
              @click="openEditor(item)"
            />

            <v-tooltip
              :text="item.usageCount
                ? 'Организация используется и не может быть удалена'
                : 'Удалить организацию'"
              location="top"
            >
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon="mdi-delete-outline"
                    variant="text"
                    color="error"
                    size="small"
                    aria-label="Удалить организацию"
                    :disabled="Boolean(item.usageCount)"
                    @click="openDeleteDialog(item)"
                  />
                </span>
              </template>
            </v-tooltip>
          </div>
        </article>
      </div>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="620" persistent>
      <v-card>
        <v-card-title>
          {{ editedItem.id ? 'Редактирование организации' : 'Новая организация' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <v-textarea
            v-model.trim="editedItem.name"
            label="Название организации"
            variant="outlined"
            rows="2"
            auto-grow
            autofocus
            hide-details="auto"
            required
            @keydown.ctrl.enter="saveItem"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">Отмена</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            @click="saveItem"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card>
        <v-card-title>Удалить организацию?</v-card-title>
        <v-card-text>
          <p class="mb-2">
            Организация будет удалена из справочника целевого набора.
          </p>
          <strong>{{ selectedItem ? selectedItem.name : '' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="deleteItem"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'TargetSetSection',
  data() {
    return {
      items: [],
      search: '',
      loading: true,
      saving: false,
      deleting: false,
      error: '',
      dialogError: '',
      editorDialog: false,
      deleteDialog: false,
      selectedItem: null,
      editedItem: {
        id: 0,
        name: ''
      }
    };
  },
  computed: {
    filteredItems() {
      const search = (this.search || '').trim().toLocaleLowerCase();

      if (!search) {
        return this.items;
      }

      return this.items.filter((item) =>
        String(item.name || '').toLocaleLowerCase().includes(search)
      );
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getTargetOrganizations'
        )
        .then((response) => {
          this.items = response.data.items || [];
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить организации целевого набора.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openEditor(item = null) {
      this.dialogError = '';
      this.editedItem = item
        ? { id: Number(item.id), name: item.name || '' }
        : { id: 0, name: '' };
      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
    },
    saveItem() {
      const name = (this.editedItem.name || '').trim();

      if (!name) {
        this.dialogError = 'Введите название организации.';
        return;
      }

      this.saving = true;
      this.dialogError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveTargetOrganization',
          new URLSearchParams({
            id: String(this.editedItem.id || 0),
            name
          })
        )
        .then((response) => {
          const savedItem = response.data.item;
          const index = this.items.findIndex(
            (item) => Number(item.id) === Number(savedItem.id)
          );

          if (index === -1) {
            this.items.push(savedItem);
          } else {
            this.items.splice(index, 1, savedItem);
          }

          this.items.sort((first, second) =>
            first.name.localeCompare(second.name, 'ru')
          );
          this.closeEditor();
        })
        .catch((error) => {
          this.dialogError = 'Не удалось сохранить организацию.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openDeleteDialog(item) {
      // Используемая организация остаётся доступной для исторических связей
      // и не может быть удалена из справочника.
      if (item.usageCount) {
        return;
      }

      this.selectedItem = item;
      this.deleteDialog = true;
    },
    deleteItem() {
      if (!this.selectedItem) {
        return;
      }

      this.deleting = true;
      this.error = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/deleteTargetOrganization',
          new URLSearchParams({ id: String(this.selectedItem.id) })
        )
        .then(() => {
          this.items = this.items.filter(
            (item) => Number(item.id) !== Number(this.selectedItem.id)
          );
          this.deleteDialog = false;
          this.selectedItem = null;
        })
        .catch((error) => {
          this.error =
            'Не удалось удалить организацию. Возможно, она уже используется.';
          this.deleteDialog = false;
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    }
  }
};
</script>

<style scoped>
.target-organizations {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.target-organizations__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.target-organizations__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.target-organizations__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.target-organizations__content {
  padding: 24px 28px 28px;
}

.target-organizations__alert,
.target-organizations__search {
  margin-bottom: 20px;
}

.target-organizations__list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.target-organizations__item {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 68px;
  padding: 10px 14px;
  border-bottom: 1px solid #edf0f4;
}

.target-organizations__item:last-child {
  border-bottom: 0;
}

.target-organizations__item-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 10px;
  background: #eaf3fc;
  color: #1565c0;
}

.target-organizations__item-name {
  color: #344054;
  font-weight: 500;
}

.target-organizations__usage {
  margin-top: 5px;
}

.target-organizations__item-actions {
  display: flex;
  align-items: center;
}

.target-organizations__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 190px;
  color: #667085;
}

.target-organizations__empty strong,
.target-organizations__empty span {
  display: block;
}

.target-organizations__empty strong {
  margin-bottom: 4px;
  color: #344054;
}

@media (max-width: 600px) {
  .target-organizations__header {
    align-items: stretch;
    flex-direction: column;
  }

  .target-organizations__header,
  .target-organizations__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .target-organizations__item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .target-organizations__item-icon {
    display: none;
  }
}
</style>
